import type {
  BulkGroupAction,
  BulkGroupResultStatus,
  IBulkGroupActionResult,
} from '@hit-game/shared/types';
import { AlertTriangle, CheckCircle2, MinusCircle, XCircle } from 'lucide-react';

import { Modal } from '../common/Modal';

interface BulkResultModalProps {
  /** `null` khi đóng. */
  data: { action: BulkGroupAction; results: IBulkGroupActionResult[] } | null;
  onClose: () => void;
}

const VERB: Record<BulkGroupAction, string> = { start: 'Start', stop: 'Stop' };

const STATUS_META: Record<
  BulkGroupResultStatus,
  { label: string; icon: typeof CheckCircle2; row: string; text: string }
> = {
  ok: {
    label: 'Thành công',
    icon: CheckCircle2,
    row: 'bg-success/10',
    text: 'text-success-bright',
  },
  skipped: { label: 'Bỏ qua', icon: MinusCircle, row: 'bg-warning/10', text: 'text-warning' },
  error: { label: 'Lỗi', icon: XCircle, row: 'bg-danger/10', text: 'text-danger' },
};

/** Thứ tự hiển thị: thành công trước, rồi bỏ qua, lỗi cuối để dễ soi. */
const ORDER: BulkGroupResultStatus[] = ['ok', 'skipped', 'error'];

/**
 * Bảng kết quả sau khi chạy bulk — từng nhóm một, gom theo trạng thái.
 *
 * Server luôn trả 200 kèm mảng này kể cả khi mọi nhóm bị bỏ qua, nên đây là chỗ
 * DUY NHẤT nói được nhóm nào chạy, nhóm nào bị bỏ (vì sao), nhóm nào hỏng.
 */
export function BulkResultModal({ data, onClose }: BulkResultModalProps) {
  if (!data) return null;

  const { action, results } = data;
  const counts = (status: BulkGroupResultStatus): number =>
    results.filter((r) => r.status === status).length;

  const sorted = [...results].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {VERB[action]} hàng loạt
          <span className="text-ink-dim text-[12px] font-normal">
            {counts('ok')} chạy · {counts('skipped')} bỏ qua · {counts('error')} lỗi
          </span>
        </span>
      }
      footer={
        <button
          type="button"
          onClick={onClose}
          className="bg-accent rounded-lg px-4 py-2 text-[13px] font-semibold text-white transition-all hover:brightness-110"
        >
          Xong
        </button>
      }
    >
      <ul className="space-y-1">
        {sorted.map((result) => {
          const meta = STATUS_META[result.status];
          const Icon = meta.icon;
          return (
            <li
              key={result.groupId}
              className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] ${meta.row}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${meta.text}`} />
              <span className="text-ink font-semibold">{result.name}</span>
              <span className={`ml-auto text-right text-[12px] ${meta.text}`}>
                {result.reason ?? meta.label}
              </span>
            </li>
          );
        })}
      </ul>

      {counts('error') > 0 && (
        <div className="bg-danger/10 border-danger-border text-danger mt-3 flex items-start gap-2 rounded-xl border p-3 text-[12px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Nhóm lỗi giữ nguyên trạng thái cũ — thử lại từng nhóm để xem chi tiết.</span>
        </div>
      )}
    </Modal>
  );
}
